import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, Loader2Icon } from 'lucide-react';
import { FormField, inputClass } from './FormField';
import { FileDrop } from './FileDrop';
import { Usage, useContactForm } from './useContactForm';
import { PlusButton } from '../../ui/PlusButton';
import { universes } from '../../../data/universes';
const projectTypes = [...universes.map(u => ({
  id: u.id,
  label: u.short
})), {
  id: 'autre',
  label: 'Autre'
}];
const usages: {
  id: Usage;
  label: string;
}[] = [{
  id: 'interieur',
  label: 'Intérieur'
}, {
  id: 'exterieur',
  label: 'Extérieur'
}, {
  id: 'mixte',
  label: 'Les deux'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function ContactForm() {
  const f = useContactForm();
  const today = new Date().toISOString().split('T')[0];
  return <div className="border border-line bg-paper p-6 md:p-10" data-id="element-51" data-mp="29tc0ggjlzeg6">
      <AnimatePresence mode="wait" initial={false} data-id="element-52" data-mp="2esooh4m7urkc">
        {f.status === 'success' ? <motion.div key="success" initial={{
        opacity: 0,
        scale: 0.98
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease
      }} className="flex min-h-[520px] flex-col items-start justify-center" role="status" data-id="element-53" data-mp="3aa5ejffkl64r">
            <span className="flex h-12 w-12 items-center justify-center bg-ember text-white" data-id="element-54" data-mp="2wiii9flmnxlz">
              <CheckIcon className="h-6 w-6" aria-hidden data-id="element-55" data-mp="2rkqiy0inl97r" />
            </span>
            <h3 className="mt-6 text-3xl font-medium tracking-[-0.03em]" data-id="element-56" data-mp="2mkksuos17urs">Demande transmise.</h3>
            <p className="mt-3 max-w-md leading-relaxed text-mute" data-id="element-57" data-mp="2hkzlbpysqe3g">
              Merci {f.values.name.split(' ')[0]}. Notre bureau d’études revient vers vous sous 48 h ouvrées avec une
              première faisabilité{f.files.length > 0 ? ` — ${f.files.length} fichier(s) bien reçu(s)` : ''}.
            </p>
            <p className="mt-6 font-mono text-sm" data-id="element-58" data-mp="btuhzh15fgfr">
              Référence <span className="bg-canvas px-2 py-1" data-id="element-59" data-mp="25bs25ubgoa9z">{f.ticket}</span>
            </p>
            <div className="mt-8" data-id="element-60" data-mp="259p02bx1aqow">
              <PlusButton variant="ink" onClick={f.reset} data-id="element-61" data-mp="2a2i5z348o52k">
                Nouvelle demande
              </PlusButton>
            </div>
          </motion.div> : <motion.form key="form" noValidate onSubmit={f.submit} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="space-y-7" data-id="element-62" data-mp="20cf56gqvlmto">
            <fieldset data-id="element-63" data-mp="2f1bkmyv9i24b">
              <legend className="mb-3 text-sm font-medium" data-id="element-64" data-mp="2f24jzerw5pcf">
                Type de projet<span className="text-ember" data-id="element-65" data-mp="2k0ef5pdm8py1"> *</span>
              </legend>
              <div role="radiogroup" aria-label="Type de projet" className="flex flex-wrap gap-2" data-id="element-66" data-mp="2k2iud3gytbdm">
                {projectTypes.map(t => {
              const selected = f.values.universe === t.id;
              return <button key={t.id} type="button" role="radio" aria-checked={selected} onClick={() => f.setField('universe', t.id)} className={`whitespace-nowrap border px-3.5 py-2 text-sm transition-colors duration-150 ${selected ? 'border-ink bg-ink text-white' : 'border-line bg-paper hover:border-ink/40'}`} data-id="element-67" data-mp="103phetrl47re">
                      {t.label}
                    </button>;
            })}
              </div>
              {f.errors.universe && <p role="alert" className="mt-1.5 text-sm text-ember-dark" data-id="element-68" data-mp="r5dkka04kdqi">
                  {f.errors.universe}
                </p>}
            </fieldset>

            <div className="grid gap-5 sm:grid-cols-2" data-id="element-69" data-mp="3ur2nhmqoqixi">
              <FormField id="name" label="Nom" required error={f.errors.name} data-id="element-70" data-mp="3pu2lpo0vc2c7">
                <input id="name" className={inputClass} value={f.values.name} onChange={e => f.setField('name', e.target.value)} aria-invalid={!!f.errors.name} autoComplete="name" data-id="element-71" data-mp="2t25r0jvgr96o" />
              </FormField>
              <FormField id="company" label="Société / structure" data-id="element-72" data-mp="36um124ms87h3">
                <input id="company" className={inputClass} value={f.values.company} onChange={e => f.setField('company', e.target.value)} autoComplete="organization" data-id="element-73" data-mp="3bsxbaggzojzu" />
              </FormField>
              <FormField id="email" label="E-mail" required error={f.errors.email} data-id="element-74" data-mp="3ps6gkl3nptp1">
                <input id="email" type="email" className={inputClass} value={f.values.email} onChange={e => f.setField('email', e.target.value)} aria-invalid={!!f.errors.email} autoComplete="email" data-id="element-75" data-mp="3pq3ozwmuvsi7" />
              </FormField>
              <FormField id="phone" label="Téléphone" data-id="element-76" data-mp="1j2mjkua82dz7">
                <input id="phone" type="tel" className={inputClass} value={f.values.phone} onChange={e => f.setField('phone', e.target.value)} autoComplete="tel" data-id="element-77" data-mp="1e4l4sljtx0tj" />
              </FormField>
            </div>

            <div className="grid gap-5 sm:grid-cols-3" data-id="element-78" data-mp="21v6zzc8qiqm4">
              <FormField id="dimensions" label="Dimensions approx." data-id="element-79" data-mp="1xsty4yx3zcum">
                <select id="dimensions" className={inputClass} value={f.values.dimensions} onChange={e => f.setField('dimensions', e.target.value)} data-id="element-80" data-mp="1xv6j7ky5y3bw">
                  <option value="" data-id="element-81" data-mp="hekict6xmwfe">Non définies</option>
                  <option value="lt2" data-id="element-82" data-mp="hgwuuqtx8el2">Moins de 2 m</option>
                  <option value="2-5" data-id="element-83" data-mp="ix0whup6tciu">2 à 5 m</option>
                  <option value="5-10" data-id="element-84" data-mp="nv2gqiycnhvw">5 à 10 m</option>
                  <option value="gt10" data-id="element-85" data-mp="nukcp05g3zdf">Plus de 10 m</option>
                </select>
              </FormField>
              <div data-id="element-86" data-mp="9v1hf7xqgiij">
                <p className="mb-2 text-sm font-medium" id="usage-label" data-id="element-87" data-mp="9xnnf34z6pyn">
                  Usage
                </p>
                <div role="radiogroup" aria-labelledby="usage-label" className="grid h-12 grid-cols-3 border border-line" data-id="element-88" data-mp="5roxqyz8xtw">
                  {usages.map(u => {
                const selected = f.values.usage === u.id;
                return <button key={u.id} type="button" role="radio" aria-checked={selected} onClick={() => f.setField('usage', u.id)} className={`whitespace-nowrap px-1 text-[13px] transition-colors duration-150 [&:not(:last-child)]:border-r [&:not(:last-child)]:border-line ${selected ? 'bg-ink text-white' : 'bg-paper hover:bg-canvas'}`} data-id="element-89" data-mp="3mej6o8sfmvmf">
                        {u.label}
                      </button>;
              })}
                </div>
              </div>
              <FormField id="deadline" label="Livraison souhaitée" data-id="element-90" data-mp="33n4cumj67bx8">
                <input id="deadline" type="date" min={today} className={inputClass} value={f.values.deadline} onChange={e => f.setField('deadline', e.target.value)} data-id="element-91" data-mp="3hn5ew5b68pea" />
              </FormField>
            </div>

            <FormField id="message" label="Votre projet" required error={f.errors.message} data-id="element-92" data-mp="3mh04fpdgjyla">
              <textarea id="message" rows={4} className={`${inputClass} h-auto resize-y py-3`} placeholder="Contexte, usage, contraintes de transport ou de montage…" value={f.values.message} onChange={e => f.setField('message', e.target.value)} aria-invalid={!!f.errors.message} data-id="element-93" data-mp="49k8f1zra1vo" />
            </FormField>

            <FileDrop files={f.files} error={f.fileError} onAdd={f.addFiles} onRemove={f.removeFile} data-id="element-94" data-mp="1km63chr6ah8y" />

            {f.status === 'error' && <p role="alert" className="bg-ember-soft px-4 py-3 text-sm text-ember-dark" data-id="element-95" data-mp="nxgnsy30q0q5">
                L’envoi a échoué. Réessayez ou écrivez-nous à etudes@ateliervolta.fr.
              </p>}

            <div className="flex flex-col-reverse gap-4 border-t border-line pt-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-96" data-mp="wvabxlqmpo28">
              <p className="text-sm text-mute" data-id="element-97" data-mp="4865rdfsv3ov">Vos fichiers restent strictement confidentiels.</p>
              <PlusButton type="submit" size="lg" disabled={f.status === 'submitting'} data-id="element-98" data-mp="979bz17infyf">
                {f.status === 'submitting' ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden data-id="element-99" data-mp="1n867uxl9fi1" />
                    Envoi en cours…
                  </> : 'Envoyer au bureau d’études'}
              </PlusButton>
            </div>
          </motion.form>}
      </AnimatePresence>
    </div>;
}